/**
 * FeaturedRitualist Component
 * Spotlight section: large portrait image left, profile details + quote right
 * Figma: 1300x500 card. Image 500x500. Right content starts at x=550, y=105.
 * Buttons (chat, person, VIEW PROFILE) are top-right aligned with name.
 */

import { memo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChatBubbleIcon, HeartFilledIcon, HeartOutlineIcon } from "@/components/icons"
import { formatLocation } from "@/components/shared/formatLocation"
import { TagList } from "@/components/shared/TagList"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"
import type { FeaturedRitualist as FeaturedRitualistType } from "../types/connect.types"

interface FeaturedRitualistProps {
  ritualist: FeaturedRitualistType | null
  onFollow?: (userId: string) => void
  onUnfollow?: (userId: string) => void
}

export const FeaturedRitualist = memo(function FeaturedRitualist({
  ritualist,
  onFollow,
  onUnfollow,
}: FeaturedRitualistProps) {
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const { t } = useTranslation("common")
  const { mutate: createConversation, isPending: isCreatingConversation } =
    useCreateDmConversation()

  if (!ritualist) return null

  const location = formatLocation(ritualist.city, ritualist.state)

  return (
    <section className="mb-10 max-w-360 mx-auto px-5 md:px-17.5">
      {/* Header */}
      <h2 className="uppercase text-xl font-semibold md:section-heading text-text-primary mb-1">
        {t("connect.featuredRitualist")}
      </h2>
      <p className="text-[13px] md:text-[14px] text-primary">{t("connect.curatedDescription")}</p>

      {/* Card: two-column on desktop, stacked on mobile */}
      <div className="flex flex-col lg:flex-row mt-5 md:mt-8">
        {/* Left: Image -- 500x500, rounded-[20px] */}
        <div className="w-full h-[350px] lg:w-[500px] lg:h-[500px] flex-shrink-0">
          {ritualist.coverImage ? (
            <img
              src={ritualist.coverImage}
              alt={ritualist.name}
              loading="lazy"
              className="w-full h-full object-cover rounded-[20px]"
            />
          ) : ritualist.profilePictureUrl ? (
            <img
              src={ritualist.profilePictureUrl}
              alt={ritualist.name}
              loading="lazy"
              className="w-full h-full object-cover rounded-[20px]"
            />
          ) : (
            <img
              src={getAssetUrl("upload-bg.jpeg")}
              alt=""
              className="w-full h-full object-cover rounded-[20px]"
            />
          )}
        </div>

        {/* Right: Details -- starts at y=105 from card top, x=50px from image */}
        <div className="flex-1 mt-5 md:mt-6 lg:pt-[95px] lg:pl-[50px] flex flex-col relative">
          {/* Top row: Name left, buttons right */}
          <div className="flex items-start flex-wrap gap-2 justify-between mb-5 md:mb-2">
            <div className="flex flex-col">
              <h3 className="text-lg md:text-[24px] font-semibold text-text-primary/90 leading-[30px] mt-3">
                {ritualist.name}
              </h3>
              {/* Stats -- 13px */}
              <div className="flex items-center gap-[10px] text-[13px] text-text-primary mt-1">
                <span>
                  {ritualist.memberCount} {t("connect.members")}
                </span>
                <span>
                  {ritualist.ritualCount} {t("connect.rituals")}
                </span>
              </div>
            </div>

            {/* Action buttons -- aligned with name */}
            <div className="flex items-center gap-5">
              {/* Hide chat and follow buttons if viewing own profile */}
              {user?.id !== ritualist.id && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="w-[44px] h-[44px] rounded-[10px] bg-connect-glass-bg border border-connect-glass-border flex items-center justify-center hover:bg-white/10 transition-colors border-gradient-3d cursor-pointer disabled:opacity-50"
                    onClick={() => {
                      if (!user) {
                        setShowLoginModal(true)
                        return
                      }
                      if (ritualist) {
                        createConversation(ritualist.id, {
                          onSuccess: conversation => {
                            navigate(`/chats/dm/${conversation.id}`)
                          },
                        })
                      }
                    }}
                    disabled={isCreatingConversation}
                    title={t("connect.chat")}
                  >
                    <ChatBubbleIcon className="w-[22px] h-[21px] text-text-primary" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        setShowLoginModal(true)
                        return
                      }
                      ritualist &&
                        (ritualist.isFollowing
                          ? onUnfollow?.(ritualist.id)
                          : onFollow?.(ritualist.id))
                    }}
                    className="w-[44px] h-[44px] rounded-[10px] bg-connect-glass-bg border border-connect-glass-border flex items-center justify-center hover:bg-white/10 transition-colors border-gradient-3d cursor-pointer"
                    aria-label={ritualist.isFollowing ? t("connect.unfollow") : t("connect.follow")}
                  >
                    {ritualist.isFollowing ? (
                      <HeartFilledIcon className="w-[20px] h-[20px] text-red-500" />
                    ) : (
                      <HeartOutlineIcon className="w-[20px] h-[20px] text-text-primary" />
                    )}
                  </button>
                </div>
              )}
              <button
                type="button"
                className="h-[50px] w-[180px] hidden md:block rounded-full bg-connect-cream text-[14px] font-semibold uppercase text-connect-cream-text hover:bg-connect-cream-hover disabled:opacity-20 transition-colors cursor-pointer"
                onClick={() => navigate(`/profile/${ritualist.id}`)}
              >
                {t("connect.viewProfile")}
              </button>
            </div>
          </div>

          {/* Location -- 13px */}
          {location && (
            <p className="text-[13px] text-text-primary mb-5 md:mb-7.5 md:mt-4">{location}</p>
          )}

          {/* Tags */}
          <TagList tags={ritualist.tags} className="mb-5 md:mb-[30px]" searchTab="ritualists" />

          {/* Quote -- 20px italic, leading-[35px], max-w-[750px] */}
          {ritualist.quote && (
            <blockquote className="text-[16px] md:text-[20px] italic text-white leading-[35px] max-w-[750px] mb-5 md:mb-0">
              {ritualist.quote}
            </blockquote>
          )}

          <button
            type="button"
            className="h-[50px] w-full block md:hidden rounded-full bg-connect-cream text-[14px] font-semibold uppercase text-connect-cream-text hover:bg-connect-cream-hover disabled:opacity-20 transition-colors cursor-pointer"
            onClick={() => navigate(`/profile/${ritualist.id}`)}
          >
            {t("connect.viewProfile")}
          </button>
        </div>
      </div>
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </section>
  )
})
